import { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import FeaturedProjects from '../components/FeaturedProjects';
import Testimonials from '../components/Testimonials';
import SectionHeader from '../components/SectionHeader';
import AnimatedCounter from '../components/AnimatedCounter';

const stats = [
  { number: 3, suffix: '+', label: 'Years of Experience' },
  { number: 5, suffix: '+', label: 'Projects Completed' },
  { number: 27, suffix: '+', label: 'Happy Families' },
];

const whyChooseUs = [
  {
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    title: 'Trusted Quality',
    description: 'Every project is built with premium materials and rigorous quality checks to ensure lasting durability.',
  },
  {
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'On-Time Delivery',
    description: 'We take pride in delivering projects on schedule, respecting our commitment to every homebuyer.',
  },
  {
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    title: 'Modern Design',
    description: 'Contemporary architecture blended with functional design for comfortable and stylish living spaces.',
  },
  {
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    title: 'Customer First',
    description: 'Transparent dealings, after-sales support, and a commitment to customer satisfaction at every step.',
  },
];

const VideoSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  return (
    <section className="relative py-28 lg:py-32 bg-charcoal overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 line-pattern" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16">
          <SectionHeader
            label="Watch Our Story"
            title="Upcoming Projects & Introduction"
            subtitle="Get an exclusive look at our vision, upcoming developments, and what makes Avdhoot Group the trusted choice for your dream home."
            light
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative max-w-5xl mx-auto"
        >
          {/* Decorative gold frame */}
          <div className="absolute -top-3 -left-3 w-full h-full border border-gold/15 hidden lg:block" />
          <div className="absolute -bottom-3 -right-3 w-full h-full border border-gold/15 hidden lg:block" />

          <div className="relative aspect-video bg-charcoal-dark">
            <video
              ref={videoRef}
              src={`${process.env.PUBLIC_URL}/Intro_video.mp4`}
              className="w-full h-full object-cover"
              controls={isPlaying}
              playsInline
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
            />

            {/* Custom Play Overlay */}
            {!isPlaying && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 bg-charcoal/40 flex items-center justify-center cursor-pointer group"
                onClick={handlePlay}
              >
                <div className="relative">
                  {/* Pulsing ring */}
                  <div className="absolute inset-0 w-20 h-20 sm:w-24 sm:h-24 rounded-full border border-gold/30 animate-ping" />
                  {/* Play button */}
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gold/90 group-hover:bg-gold rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110">
                    <svg className="w-8 h-8 sm:w-10 sm:h-10 text-charcoal ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Home = () => {
  const ctaRef = useRef(null);
  const { scrollYProgress: ctaScroll } = useScroll({
    target: ctaRef,
    offset: ['start end', 'end start'],
  });
  const ctaY = useTransform(ctaScroll, [0, 1], ['0%', '20%']);

  return (
    <div>
      <HeroSection />

      {/* Stats Section */}
      <section className="relative bg-charcoal py-20 shimmer-bg">
        {/* Shimmer line at top */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 lg:gap-12">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="text-center"
              >
                {/* Gold accent line */}
                <div className="w-8 h-0.5 bg-gold/40 mx-auto mb-6" />
                <p className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-gradient-gold mb-2">
                  <AnimatedCounter target={stat.number} suffix={stat.suffix} />
                </p>
                <p className="text-cream-200/40 font-sans text-xs uppercase tracking-widest-plus">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Builder Section */}
      <section className="py-28 lg:py-32 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="relative">
                {/* Decorative gold frame offset */}
                <div className="absolute -top-4 -left-4 w-full h-full border-2 border-gold/20 hidden lg:block" />
                <img
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80"
                  alt="Avdhoot Group Building"
                  className="relative w-full h-[500px] object-cover"
                />
                {/* Dot pattern element */}
                <div className="absolute -bottom-6 -right-6 w-24 h-24 dot-pattern opacity-10 hidden lg:block" />
                {/* Years badge */}
                <div className="absolute -bottom-6 -right-6 bg-charcoal border border-gold/20 text-white p-8 hidden lg:block">
                  <p className="text-4xl font-serif font-bold text-gradient-gold">3+</p>
                  <p className="text-xs font-sans uppercase tracking-widest-plus text-cream-200/50 mt-1">Years of<br />Excellence</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <SectionHeader
                label="About Us"
                title="Burhanpur's Premier Real Estate Developer"
                align="left"
              />
              <p className="text-charcoal/60 font-sans leading-relaxed mb-6 mt-6">
                Since 2023, Avdhoot Group has been transforming the skyline of Burhanpur with premium residential and commercial projects. Our commitment to quality construction, innovative design, and timely delivery has earned us the trust of over 27 families.
              </p>
              <p className="text-charcoal/60 font-sans leading-relaxed mb-8">
                We believe that every home should be a masterpiece. From luxurious villas to modern apartments, each project reflects our dedication to creating spaces that inspire and elevate everyday living.
              </p>
              <Link to="/about" className="btn-premium">
                <span>Learn More About Us</span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro Video Section */}
      <VideoSection />

      {/* Featured Projects */}
      <FeaturedProjects />

      {/* Why Choose Us */}
      <section className="py-28 lg:py-32 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-20">
            <SectionHeader
              label="Our Strengths"
              title="Why Choose Avdhoot Group"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="group card-premium p-8 text-center border-l-0 hover:border-l-2 hover:border-l-gold"
              >
                <div className="w-16 h-16 bg-gold/5 text-gold mx-auto mb-6 flex items-center justify-center group-hover:bg-gold group-hover:text-charcoal transition-all duration-500 ease-luxury">
                  {item.icon}
                </div>
                <h3 className="text-lg font-serif font-semibold text-charcoal mb-3">{item.title}</h3>
                <p className="text-charcoal/50 font-sans text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* CTA Section */}
      <section ref={ctaRef} className="relative py-28 lg:py-32 overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: ctaY }}>
          <img
            src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80"
            alt="Luxury interior"
            className="w-full h-[130%] object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-charcoal/85" />

        {/* Decorative corner frames */}
        <div className="hidden lg:block absolute top-12 left-12 w-32 h-32 border-t border-l border-gold/15" />
        <div className="hidden lg:block absolute bottom-12 right-12 w-32 h-32 border-b border-r border-gold/15" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center gap-4 justify-center mb-6">
              <span className="h-px w-8 bg-gold/40" />
              <span className="text-xs font-sans font-medium uppercase tracking-widest-plus text-gold-light">
                Start Your Journey
              </span>
              <span className="h-px w-8 bg-gold/40" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-white mb-6">
              Ready to Find Your Dream Home?
            </h2>
            <div className="gold-divider mx-auto mb-6" />
            <p className="text-cream-200/60 font-sans text-lg max-w-2xl mx-auto mb-10">
              Let us help you discover the perfect property. Schedule a site visit today and experience the Avdhoot Group difference.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-premium-filled">
                <span>Schedule a Visit</span>
              </Link>
              <a
                href="tel:+919479303333"
                className="btn-premium border-white/30 text-white hover:text-charcoal"
              >
                <span>Call +91 94793 03333</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
